import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level cache so multiple components rendering the same menu
// don't re-fetch on every mount. The cache is invalidated on full page
// reload — which is fine for menus that change once a week, not once a
// second.
const _cache = new Map(); // key -> {groups, fetched_at}

/**
 * useMenuFromDB(key, fallbackGroups)
 *
 * Fetches /api/cms/menus/<key> on mount. Returns the DB groups if one
 * exists; otherwise returns the static `fallbackGroups` so the menu
 * NEVER renders empty during a backend hiccup or before an admin has
 * ever written a row.
 */
export function useMenuFromDB(key, fallbackGroups) {
  const [groups, setGroups] = useState(() => _cache.get(key)?.groups || fallbackGroups);

  useEffect(() => {
    let cancelled = false;
    // If we already have a cached fetch <60s old, skip.
    const cached = _cache.get(key);
    if (cached && Date.now() - cached.fetched_at < 60_000) return;

    axios
      .get(`${API}/cms/menus/${key}`)
      .then((r) => {
        if (cancelled) return;
        const next = Array.isArray(r.data?.groups) ? r.data.groups : null;
        if (next && next.length > 0) {
          _cache.set(key, { groups: next, fetched_at: Date.now() });
          setGroups(next);
        }
      })
      .catch(() => {
        // 404 → no DB-backed menu yet; keep the static fallback.
      });
    return () => { cancelled = true; };
  }, [key]);

  return groups;
}
